import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  Typography,
} from '@mui/material';
import { Button } from '../../../../components/ui';
import FileInput from '../../../../components/Shared/FileUploadPreview/FileInput.jsx';
import useTemplateSx from '../../hooks/useTemplateSx';
import { getHeaderAcceptValue } from '../../utils/templateLimitUtils';
import Field from '../ui/Field';
import RequiredLabel from '../ui/RequiredLabel';
import TemplateSendPreview from './TemplateSendPreview';
import { useOptoutCheck } from '../../services/optoutService';

export default function TemplateSendSinglePanel({ send, template, onClose }) {
  const styles = useTemplateSx();
  const { isOptedOut } = useOptoutCheck();
  const headerFormat = send.headerComponent?.format;
  const phoneNumber = send.formDataToSend?.phone_number ?? '';
  const isRecipientOptedOut = Boolean(
    phoneNumber && phoneNumber.length >= 10 && isOptedOut(phoneNumber)
  );
  const canSend = phoneNumber.length >= 10 && !send.loading && !isRecipientOptedOut;

  return (
    <Box sx={styles.sendDialogGrid}>
      <Box sx={styles.sendDialogSection}>
        <Typography sx={styles.sendDialogSectionTitle}>Single Recipient</Typography>
        <Typography sx={styles.sendDialogSectionHint}>
          Enter a phone number with country code (e.g. 919876543210) and fill template variables.
        </Typography>

        {send.sendError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {send.sendError}
          </Alert>
        ) : null}

        {isRecipientOptedOut ? (
          <Alert severity="error" sx={{ mb: 2, fontWeight: 500 }}>
            ⚠️ This user has opted out of WhatsApp messages. Template cannot be sent to this number.
          </Alert>
        ) : null}

        <Field
          stacked
          label={<RequiredLabel>Phone Number</RequiredLabel>}
          value={phoneNumber}
          onChange={(e) => send.handlePhoneNumberChange(e.target.value)}
          error={Boolean(send.validationError?.phone_number)}
          errorMessage={send.validationError?.phone_number}
          helperText={!send.validationError?.phone_number ? 'Include country code without +' : undefined}
          maxLength={13}
          inputMode="numeric"
          placeholder="919876543210"
        />

        {send.getUserPhoneNumber?.() ? (
          <Box mt={1}>
            <Button variant="outline" size="sm" onClick={send.fillMyPhoneNumber} disabled={send.loading}>
              Send to my number
            </Button>
          </Box>
        ) : null}

        {send.bodyParams?.length > 0 ? (
          <Box mt={2}>
            <Typography sx={styles.sendDialogSectionTitle}>Body Parameters</Typography>
            <Grid container spacing={1.5} mt={0.5}>
              {send.bodyParams.map((item, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Field
                    stacked
                    label={item?.label || `Parameter ${index + 1}`}
                    value={item?.text || ''}
                    onChange={(e) => send.handleChangeParams(e.target.value, index)}
                    error={Boolean(send.validationError?.[`bodyParams_${index}`])}
                    errorMessage={send.validationError?.[`bodyParams_${index}`]}
                    placeholder={`Enter ${item?.label || `value ${index + 1}`}`}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : null}

        {send.headerComponent && headerFormat !== 'TEXT' ? (
          <Box mt={2}>
            <Typography sx={styles.sendDialogSectionTitle}>
              Header {headerFormat?.toLowerCase()}
            </Typography>
            {!send.useCreateImage ? (
              <>
                <FileInput
                  onFileSelect={send.onSelectHeaderFile}
                  onFileRemove={send.onHeaderFileRemove}
                  file={send.headerFile}
                  setFile={send.setHeaderFile}
                  accept={getHeaderAcceptValue(headerFormat)}
                />
                <FormHelperText sx={{ mt: 1 }}>
                  {headerFormat === 'IMAGE' && 'Allowed: JPG, PNG • Max 5 MB'}
                  {headerFormat === 'VIDEO' && 'Allowed: MP4, 3GP • Max 16 MB'}
                  {headerFormat === 'DOCUMENT' &&
                    'Allowed: PDF, DOC, DOCX • Max 95 MB'}
                </FormHelperText>
                {send.validationError?.headerFile ? (
                  <FormHelperText error sx={{ mt: 0.5 }}>
                    {send.validationError.headerFile}
                  </FormHelperText>
                ) : null}
              </>
            ) : null}
            {template?.media?.header ? (
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    checked={send.useCreateImage}
                    onChange={(e) => send.handleUseCreateImageChange(e.target.checked)}
                  />
                }
                label={`Use the template's uploaded ${headerFormat?.toLowerCase()} header`}
              />
            ) : null}
          </Box>
        ) : null}

        {send.buttonsWithExamples.length > 0 ? (
          <Box mt={2}>
            <Typography sx={styles.sendDialogSectionTitle}>Button Parameters</Typography>
            <Grid container spacing={1.5} mt={0.5}>
              {send.buttonsWithExamples.map((button) => {
                const index = button.originalIndex ?? 0;
                return (
                  <Grid item xs={12} key={index}>
                    <Field
                      stacked
                      label={button?.text || `Button ${index + 1}`}
                      value={send.buttonUrlParams[index] || ''}
                      onChange={(e) => send.handleButtonUrlChange(index, e.target.value)}
                      error={Boolean(send.validationError?.[`buttonUrlParams_${index}`])}
                      errorMessage={send.validationError?.[`buttonUrlParams_${index}`]}
                      placeholder="Enter dynamic URL suffix"
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ) : null}

        <Box sx={styles.sendDialogActions}>
          <Button variant="outline" size="sm" onClick={onClose} disabled={send.loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={send.loading}
            disabled={!canSend}
            onClick={send.sendSingle}
          >
            Send
          </Button>
        </Box>
      </Box>

      <TemplateSendPreview template={template} bodyParams={send.bodyParams} />
    </Box>
  );
}
